import React, { useEffect, useState } from "react";
import { confirm } from "../utils/confirm";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDoc, setDoc, getDocs } from "firebase/firestore";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Plus, X, FileText, Printer, ChevronDown, ChevronRight } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { comprimiImmagini } from "../utils/imageUtils";
import { durataOre } from "../utils/oreRapportini";
import { numIt } from "../utils/economiaCommessa";
import { SelettoreSede } from "./SediCliente";
import { copiaLibera, descrizioneSede } from "../utils/sediCliente";
import FirmaPad, { FirmaImmagine } from "./FirmaPad";
import { apriDocumento, testataHtml, piedeHtml, esc, dataLunga } from "../utils/cartaIntestata";
import { statoBlocco, verificaModificaRapportino, AVVISO_AGGIUNGI_FIRMA_CLIENTE } from "../utils/bloccoRapportini";
import { chiaveBozza, prefissoBozze, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza, { ElencoBozze, delGiorno } from "./AvvisoBozza";
import { UsaMiaFirma } from "./MiaFirma";

const STATI = {
  bozza:   { label: "Bozza",   bg: "#f3f4f6", col: "#6b7280" },
  firmato: { label: "Firmato", bg: "#d1fae5", col: "#059669" },
  inviato: { label: "Inviato", bg: "#dbeafe", col: "#2563eb" },
};

export const STATI_INTERVENTO = {
  risolto:            { label: "✅ Risolto",                    bg: "#d1fae5", col: "#065f46" },
  parzialmente:       { label: "⚠️ Parzialmente risolto",       bg: "#fef3c7", col: "#92400e" },
  non_risolto:        { label: "❌ Non risolto",                bg: "#fee2e2", col: "#991b1b" },
  nuovo_intervento:   { label: "🔄 Necessario nuovo intervento",bg: "#ede9fe", col: "#5b21b6" },
  attesa_materiale:   { label: "📦 In attesa di materiale",     bg: "#f0f9ff", col: "#0369a1" },
  attesa_autorizzaz:  { label: "🔐 In attesa di autorizzazione",bg: "#fdf4ff", col: "#7e22ce" },
};

// ── Canvas firma ────────────────────────────────────────────────────────────
// Firma: componente unico di tutta l'app (components/FirmaPad.js)
function FirmaCanvas({ onSave, onCancel, label }) {
  return <FirmaPad label={label || "Firma del cliente"} onSave={onSave} onCancel={onCancel}/>;
}

// Ore per operatore salvate uguali alla durata dell'intervento = erano automatiche (campo
// lasciato vuoto): riaprendo il rapportino tornano vuote, così se si correggono gli orari le
// ore seguono la nuova durata invece di restare ferme al valore vecchio.
function oreAutomaticheLibere(r) {
  const per = r?.orePerOperatore;
  if (!per || typeof per !== "object") return r;
  const durata = numIt(r.durataOre) > 0 ? numIt(r.durataOre) : durataOre(r.oraInizio, r.oraFine);
  if (!(durata > 0)) return r;
  const libere = {};
  Object.entries(per).forEach(([k, v]) => { if (Math.abs(numIt(v) - durata) >= 0.01) libere[k] = v; });
  return { ...r, orePerOperatore: libere };
}

// ── Modal nuovo/modifica rapportino ─────────────────────────────────────────
function RapportinoModal({ rapportino, projectId, projectName, codiceCommessa, orgId, orgInfo, utenti, onClose, commessa, riprendiBozza = false }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const oggi = toLocalDateStr(new Date());
  // Un rapportino già salvato può non avere tutti i campi (es. nato da una manutenzione o
  // da una versione precedente): gli elenchi mancanti diventano vuoti, altrimenti aprendo
  // «Modifica» la finestra si bloccava.
  const [form, setForm] = useState(() => rapportino ? oreAutomaticheLibere({
    ...rapportino,
    lavorazioni: rapportino.lavorazioni || "",
    materiali: Array.isArray(rapportino.materiali) ? rapportino.materiali : [],
    operatori: Array.isArray(rapportino.operatori) ? rapportino.operatori : [],
    operatoriUid: Array.isArray(rapportino.operatoriUid) ? rapportino.operatoriUid : [],
    foto: Array.isArray(rapportino.foto) ? rapportino.foto : [],
  }) : {
    data: oggi, oraInizio: "", oraFine: "",
    nomeCliente: commessa?.clienteNome || "", sitoOggetto: "", sede: null,
    operatori: [currentUser?.displayName || ""],
    operatoriUid: [currentUser?.uid || ""],
    motivoIntervento: "", tipologiaIntervento: "ordinaria",
    problemRiscontrato: "", diagnosi: "",
    lavorazioni: "", materiali: [{ descrizione: "", quantita: "" }],
    soluzionAdottata: "",
    anomalieResidui: "", attivitaRestanti: "",
    esitoIntervento: "risolto",
    criticita: "", soluzione: "", note: "",
    foto: [],
    firmaCliente: null, nomeFirmatario: "", firmaTecnico: null, compilatoreRegistro: "", stato: "bozza",
  });
  // La commessa arriva dopo l'apertura del modulo: il nome del cliente si completa appena c'è
  // (solo su un rapportino nuovo e se non è già stato scritto).
  useEffect(() => {
    if (!rapportino && commessa?.clienteNome) setForm(f => f.nomeCliente ? f : { ...f, nomeCliente: commessa.clienteNome });
  }, [commessa?.clienteNome]);
  const [showFirma, setShowFirma] = useState(false);
  const [showFirmaTecnico, setShowFirmaTecnico] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js): se il telefono chiude la pagina
  // a metà compilazione, il rapportino si ritrova com'era.
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "rapportino", projectId, rapportino?.id),
    valore: form,
    info: { data: form.data, numero: rapportino?.numero || "",
      titolo: `${rapportino?.numero ? `Rapportino ${rapportino.numero}` : "Nuovo rapportino"} — ${[codiceCommessa, projectName].filter(Boolean).join(" ")}`,
      vai: { page: "project", projectId, initialTab: "rapportini" } },
    applica: dati => setForm(f => ({ ...f, ...dati })),
    riprendiSubito: riprendiBozza,
  });

  const addMateriale = () => upd("materiali", [...form.materiali, { descrizione: "", quantita: "" }]);
  const updMateriale = (i, k, v) => {
    const nuovi = [...form.materiali];
    nuovi[i] = { ...nuovi[i], [k]: v };
    upd("materiali", nuovi);
  };
  const removeMateriale = (i) => upd("materiali", form.materiali.filter((_, idx) => idx !== i));

  const salva = async () => {
    if (!form.lavorazioni.trim()) { addToast("Inserisci le lavorazioni eseguite", "error"); return; }
    // Con entrambe le firme il rapportino diventa definitivo (non più modificabile né
    // eliminabile da nessuno): lo si dice PRIMA di salvare, non dopo.
    if (statoBlocco(form) === "definitivo" && statoBlocco(rapportino) !== "definitivo" &&
        !await confirm("Con la firma del tecnico e quella del cliente il rapportino diventa un documento definitivo: dopo il salvataggio nessuno potrà più modificarlo né eliminarlo. Controlla che sia tutto corretto.",
          { title: "Salvare il rapportino firmato?", type: "warning", confirmLabel: "Salva definitivo" })) return;
    const descrizioneBreve = form.lavorazioni.slice(0, 100).trim() + (form.lavorazioni.length > 100 ? "..." : "");
    // Lo stato passa da sola a "Firmato" quando sono raccolte entrambe le firme — prima
    // restava "Bozza" per sempre, anche a firme raccolte: l'etichetta colorata (bozza/
    // firmato/inviato) esisteva ma non cambiava mai. "Inviato" resta una scelta manuale
    // (non c'è oggi un invio automatico da tracciare).
    const statoCalcolato = (form.firmaTecnico && form.firmaCliente) ? "firmato" : (form.stato==="inviato" ? "inviato" : "bozza");
    // Ore lavorate da ciascun operatore: quelle scritte, oppure la durata dell'intervento.
    // È il dato che diventa costo di manodopera con fonte ore "Rapportini".
    const durata = durataOre(form.oraInizio, form.oraFine);
    const orePerOperatore = {};
    form.operatori.forEach((nome, i) => {
      const chiave = form.operatoriUid?.[i] || nome; if (!chiave) return;
      const scritto = form.orePerOperatore?.[chiave];
      const ore = (scritto !== undefined && String(scritto).trim() !== "") ? numIt(scritto) : durata;
      if (ore > 0) orePerOperatore[chiave] = ore;
    });
    const dati = { ...form, orePerOperatore, durataOre: durata, stato: statoCalcolato, projectId, projectName, codiceCommessa: codiceCommessa||"", orgId, descrizioneBreve, updatedAt: serverTimestamp() };
    if (rapportino?.id) {
      try { await updateDoc(doc(db, "rapportini", rapportino.id), dati); }
      catch (e) { addToast("Rapportino non salvato: " + (e?.message || e), "error"); return; }
      addToast("Rapportino aggiornato ✅", "success");
    } else {
      // Numerazione INT-YYYY-NNNN univoca per organizzazione
      const anno = new Date().getFullYear();
      const progressivo = await prossimoProgressivo(`${orgId}_interventi_${anno}`, { orgId, anno });
      const numero = `INT-${anno}-${String(progressivo).padStart(4, "0")}`;
      const rapRef = await addDoc(collection(db, "rapportini"), { ...dati, numero, createdAt: serverTimestamp() });
      logEvento({ orgId, tipo:"RAPPORTINO_CREATO", ...TIPI_EVENTO.RAPPORTINO_CREATO,
        titolo:`Rapportino ${numero} creato — ${dati.nomeCliente||projectName}`,
        descrizione:`Commessa: ${projectName}`,
        uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"",
        refId:rapRef.id, refTipo:"rapportino", refNome:numero,
        projectId, projectName,
        clienteNome:dati.nomeCliente||"",
        navigateTo:{page:"registro-rapportini"}
      });
      addToast(`Rapportino ${numero} creato ✅`, "success");
    }
    bozza.salvato();
    onClose();
  };

  const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };
  const ta = { ...inp, minHeight: 90, resize: "vertical" };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={15} color="#7c3aed" /> {rapportino ? "Modifica rapportino" : "Nuovo rapportino"}
            {codiceCommessa && <span style={{fontFamily:"monospace",fontWeight:800,color:"#7c3aed",fontSize:13,background:"#ede9fe",padding:"1px 8px",borderRadius:6}}>{codiceCommessa}</span>}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={15} /></button>
        </div>

        {bozza.trovata && <AvvisoBozza testo={rapportino ? "Hai modifiche non salvate a questo rapportino" : "Hai un rapportino iniziato e non salvato"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        {/* Logo e dati azienda automatici */}
        {orgInfo && (
          <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display:"flex", alignItems:"center", gap:12 }}>
            {orgInfo.logo ? <img src={orgInfo.logo} alt="Logo" style={{ height:40, objectFit:"contain" }}/> : <div style={{ fontSize:16, fontWeight:800, color:"#7c3aed" }}>{orgInfo.nome || orgInfo.name}</div>}
            <div style={{ fontSize:11, color:"#6b7280" }}>
              {/* "name" è il nome salvato alla registrazione; "nome" quello delle Impostazioni */}
              <div style={{ fontWeight:700, color:"#374151" }}>{orgInfo.nome || orgInfo.name}</div>
              {orgInfo.indirizzo && <div>{orgInfo.indirizzo}</div>}
              {orgInfo.piva && <div>P.IVA {orgInfo.piva}</div>}
            </div>
          </div>
        )}

        {/* Cliente e sito in oggetto */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div><label className="form-label">Nome cliente</label>
            <input style={inp} value={form.nomeCliente||""} onChange={e=>upd("nomeCliente",e.target.value)} placeholder="Es. Mario Rossi / Comune di Napoli"/></div>
          <div style={{gridColumn:"span 2"}}>
            {/* Luogo dell'intervento: dalle sedi del cliente (proposta quella della commessa) */}
            <SelettoreSede clienteId={commessa?.clienteId||""} etichetta="Luogo dell'intervento"
              value={form.sede || (form.sitoOggetto ? copiaLibera(form.sitoOggetto) : null)}
              proposta={!rapportino ? (commessa?.sede || null) : null}
              onChange={c=>setForm(f=>({...f, sede:c||null, sitoOggetto:descrizioneSede(c)}))}/></div>
        </div>

        {/* Commessa e data */}
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: "#6b7280" }}>Commessa: </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed" }}>{projectName}</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div><label className="form-label">Data *</label>
            <input type="date" style={inp} value={form.data} onChange={e => upd("data", e.target.value)} /></div>
          <div><label className="form-label">Ora inizio</label>
            <input type="time" style={inp} value={form.oraInizio} onChange={e => upd("oraInizio", e.target.value)} /></div>
          <div><label className="form-label">Ora fine</label>
            <input type="time" style={inp} value={form.oraFine} onChange={e => upd("oraFine", e.target.value)} /></div>
        </div>

        {/* Operatori */}
        <div className="form-group">
          <label className="form-label">Operatori presenti</label>
          {form.operatori.map((op, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
              <select style={{ ...inp, flex: 1 }} value={form.operatoriUid?.[i]||""}
                onChange={e => {
                  const uidScelto = e.target.value;
                  const u = (utenti||[]).find(x=>x.uid===uidScelto);
                  const nomi = [...form.operatori]; nomi[i] = u?.displayName || "";
                  const uids = [...(form.operatoriUid||[])]; uids[i] = uidScelto;
                  setForm(f=>({...f, operatori:nomi, operatoriUid:uids}));
                }}>
                <option value="">— Seleziona operatore —</option>
                {(utenti||[]).map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                {/* Se questo rapportino è vecchio e il nome salvato non corrisponde a nessun
                    account (o l'account non esiste più), mostralo comunque per non perdere
                    il dato — ma senza un uid non potrà mai contribuire al costo di manodopera. */}
                {op && !(utenti||[]).some(u=>u.displayName===op) && <option value="">{op} (non trovato tra gli account attivi)</option>}
              </select>
              {(() => {
                const chiave = form.operatoriUid?.[i] || op;
                const durata = durataOre(form.oraInizio, form.oraFine);
                return (
                  <input style={{ ...inp, width: 74, flex: "0 0 74px", textAlign: "right" }} inputMode="decimal"
                    title="Ore lavorate da questo operatore (se vuoto: la durata dell'intervento)"
                    disabled={!chiave}
                    value={chiave ? (form.orePerOperatore?.[chiave] ?? "") : ""}
                    placeholder={durata > 0 ? `${String(durata).replace(".", ",")} h` : "ore"}
                    onChange={e => setForm(f => ({ ...f, orePerOperatore: { ...(f.orePerOperatore||{}), [chiave]: e.target.value } }))}/>
                );
              })()}
              {form.operatori.length > 1 && <button onClick={() => {
                setForm(f=>({...f, operatori:f.operatori.filter((_, idx) => idx !== i), operatoriUid:(f.operatoriUid||[]).filter((_, idx) => idx !== i)}));
              }} style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}><X size={13} /></button>}
            </div>
          ))}
          <button onClick={() => setForm(f=>({...f, operatori:[...f.operatori,""], operatoriUid:[...(f.operatoriUid||[]),""]}))} style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif", padding: "4px 0" }}>+ Aggiungi operatore</button>
          <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>
            💡 Accanto a ogni operatore le ore lavorate: se lasci vuoto vale la durata dell'intervento (ora fine − ora inizio). Solo gli operatori scelti da questo elenco (collegati a un account Nexiora) vengono conteggiati nel costo di manodopera.
          </div>
        </div>

        {/* Tipologia intervento */}
        <div className="form-group">
          <label className="form-label">Tipologia intervento</label>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              {id:"ordinaria",   label:"Ordinaria",   bg:"#d1fae5", col:"#065f46"},
              {id:"straordinaria",label:"Straordinaria",bg:"#fef3c7",col:"#92400e"},
              {id:"predittiva",  label:"Predittiva",  bg:"#dbeafe", col:"#1d4ed8"},
              {id:"urgenza",     label:"Urgenza",     bg:"#fee2e2", col:"#991b1b"},
            ].map(t=>(
              <button key={t.id} type="button" onClick={()=>upd("tipologiaIntervento",t.id)}
                style={{padding:"5px 14px",borderRadius:20,
                  border:`1px solid ${form.tipologiaIntervento===t.id?t.col:"#e5e7eb"}`,
                  background:form.tipologiaIntervento===t.id?t.bg:"#fff",
                  color:form.tipologiaIntervento===t.id?t.col:"#6b7280",
                  fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                  fontWeight:form.tipologiaIntervento===t.id?700:400}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Motivo intervento */}
        <div className="form-group">
          <label className="form-label">Motivo dell'intervento</label>
          <textarea style={{...ta, minHeight:60}} value={form.motivoIntervento||""} onChange={e=>upd("motivoIntervento",e.target.value)} placeholder="Motivo della chiamata o richiesta di intervento..."/>
        </div>

        {/* Problema e diagnosi */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))",gap:10,marginBottom:12}}>
          <div><label className="form-label">Problema riscontrato</label>
            <textarea style={{...ta,minHeight:70}} value={form.problemRiscontrato||""} onChange={e=>upd("problemRiscontrato",e.target.value)} placeholder="Descrivi il problema rilevato..."/></div>
          <div><label className="form-label">Diagnosi</label>
            <textarea style={{...ta,minHeight:70}} value={form.diagnosi||""} onChange={e=>upd("diagnosi",e.target.value)} placeholder="Causa del problema..."/></div>
        </div>

        {/* Lavorazioni */}
        <div className="form-group">
          <label className="form-label">Lavorazioni / Attività eseguite *</label>
          <textarea style={ta} value={form.lavorazioni} onChange={e => upd("lavorazioni", e.target.value)} placeholder="Descrivi dettagliatamente le lavorazioni effettuate..." />
        </div>

        {/* Materiali */}
        <div className="form-group">
          <label className="form-label">Materiali utilizzati</label>
          {form.materiali.map((m, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <input style={inp} value={m.descrizione} onChange={e => updMateriale(i, "descrizione", e.target.value)} placeholder="Materiale..." />
              <input style={inp} value={m.quantita} onChange={e => updMateriale(i, "quantita", e.target.value)} placeholder="Qtà / unità" />
              <button onClick={() => removeMateriale(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}><X size={13} /></button>
            </div>
          ))}
          <button onClick={addMateriale} style={{ fontSize: 11, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif", padding: "4px 0" }}>+ Aggiungi materiale</button>
        </div>

        {/* Criticità */}
        <div className="form-group">
          <label className="form-label">Criticità riscontrate</label>
          <textarea style={{ ...ta, minHeight: 70 }} value={form.criticita} onChange={e => upd("criticita", e.target.value)} placeholder="Descrivi eventuali problemi o anomalie riscontrate..." />
        </div>

        {/* Soluzione proposta */}
        <div className="form-group">
          <label className="form-label">Soluzione proposta</label>
          <textarea style={{ ...ta, minHeight: 70 }} value={form.soluzione} onChange={e => upd("soluzione", e.target.value)} placeholder="Soluzione proposta o intervento consigliato..." />
        </div>

        {/* Soluzione adottata */}
        <div className="form-group">
          <label className="form-label">Soluzione adottata</label>
          <textarea style={{...ta,minHeight:70}} value={form.soluzionAdottata||""} onChange={e=>upd("soluzionAdottata",e.target.value)} placeholder="Soluzione effettivamente adottata durante l'intervento..."/>
        </div>

        {/* Anomalie residue e attività restanti */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))",gap:10,marginBottom:12}}>
          <div><label className="form-label">Anomalie residue</label>
            <textarea style={{...ta,minHeight:60}} value={form.anomalieResidui||""} onChange={e=>upd("anomalieResidui",e.target.value)} placeholder="Eventuali anomalie non risolte..."/></div>
          <div><label className="form-label">Attività ancora da eseguire</label>
            <textarea style={{...ta,minHeight:60}} value={form.attivitaRestanti||""} onChange={e=>upd("attivitaRestanti",e.target.value)} placeholder="Attività da completare in un secondo momento..."/></div>
        </div>

        {/* Esito intervento */}
        <div className="form-group">
          <label className="form-label">Esito intervento</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {Object.entries(STATI_INTERVENTO).map(([k,v])=>(
              <button key={k} type="button" onClick={()=>upd("esitoIntervento",k)}
                style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${form.esitoIntervento===k?v.col:"#e5e7eb"}`,
                  background:form.esitoIntervento===k?v.bg:"#fff",color:form.esitoIntervento===k?v.col:"#6b7280",
                  fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif",fontWeight:form.esitoIntervento===k?700:400}}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Compilatore registro */}
        <div className="form-group">
          <label className="form-label">Compilatore registro <span style={{fontWeight:400,color:"#9ca3af",fontSize:11}}>(se diverso dagli operatori)</span></label>
          <input style={inp} value={form.compilatoreRegistro||""} onChange={e=>upd("compilatoreRegistro",e.target.value)}
            placeholder="Nome e cognome di chi compila il registro"/>
        </div>

        {/* Foto */}
        <div className="form-group">
          <label className="form-label">📷 Foto (prima/durante/dopo)</label>
          <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",
            background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:7,cursor:"pointer",
            fontSize:11,fontFamily:"'Inter',sans-serif"}}>
            📁 Aggiungi foto
            <input type="file" accept="image/*" multiple style={{display:"none"}}
              onChange={async e=>{
                const files = Array.from(e.target.files);
                e.target.value = ""; // permette di ricaricare subito lo stesso file se serve
                try {
                  const compresse = await comprimiImmagini(files);
                  upd("foto",[...(form.foto||[]),...compresse]);
                } catch(err) {
                  addToast(err.message||"Errore nel caricamento di una foto","error");
                }
              }}/>
          </label>
          {(form.foto||[]).length>0 && (
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
              {(form.foto||[]).map((f,i)=>(
                <div key={i} style={{position:"relative"}}>
                  <img src={f.data} alt={f.nome} style={{width:80,height:80,objectFit:"cover",borderRadius:6,border:"1px solid #e5e7eb"}}/>
                  <button onClick={()=>upd("foto",(form.foto||[]).filter((_,idx)=>idx!==i))}
                    style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",
                      background:"#dc2626",color:"#fff",border:"none",cursor:"pointer",fontSize:12,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="form-group">
          <label className="form-label">Note aggiuntive</label>
          <textarea style={{ ...ta, minHeight: 60 }} value={form.note} onChange={e => upd("note", e.target.value)} placeholder="Eventuali note..." />
        </div>

        {/* Firma tecnico — chi ha eseguito il lavoro, distinta dalla firma del cliente */}
        <div className="form-group">
          <label className="form-label">Firma tecnico</label>
          {form.firmaTecnico ? (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#f9fafb" }}>
              <FirmaImmagine src={form.firmaTecnico} alt="Firma tecnico" style={{ maxWidth: 300, maxHeight: 100, display: "block" }} />
              <button onClick={() => upd("firmaTecnico", null)} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif", marginTop: 4 }}>Rimuovi firma</button>
            </div>
          ) : showFirmaTecnico ? (
            <FirmaCanvas label="Firma del tecnico" onSave={data => { upd("firmaTecnico", data); setShowFirmaTecnico(false); }} onCancel={() => setShowFirmaTecnico(false)} />
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
            <button onClick={() => setShowFirmaTecnico(true)}
              style={{ padding: "7px 16px", border: "1px solid #7c3aed", borderRadius: 7, background: "#ede9fe", color: "#7c3aed", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Inter',sans-serif" }}>
              ✍️ Raccogli firma tecnico
            </button>
            {/* firma salvata del tecnico (components/MiaFirma.js) */}
            <UsaMiaFirma onUsa={d => upd("firmaTecnico", d)} />
            </div>
          )}
        </div>

        {/* Firma cliente */}
        <div className="form-group">
          <label className="form-label">Firma cliente</label>
          {form.firmaCliente ? (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, background: "#f9fafb" }}>
              <FirmaImmagine src={form.firmaCliente} alt="Firma" style={{ maxWidth: 300, maxHeight: 100, display: "block" }} />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Firmato da: <strong>{form.nomeFirmatario || "—"}</strong></div>
              <button onClick={() => { upd("firmaCliente", null); upd("nomeFirmatario", ""); }} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif", marginTop: 4 }}>Rimuovi firma</button>
            </div>
          ) : showFirma ? (
            <div>
              <div className="form-group">
                <label className="form-label">Nome del firmatario</label>
                <input style={inp} value={form.nomeFirmatario} onChange={e => upd("nomeFirmatario", e.target.value)} placeholder="Nome e cognome cliente" />
              </div>
              <FirmaCanvas onSave={data => { upd("firmaCliente", data); setShowFirma(false); }} onCancel={() => setShowFirma(false)} />
            </div>
          ) : (
            <button onClick={() => setShowFirma(true)}
              style={{ padding: "7px 16px", border: "1px solid #7c3aed", borderRadius: 7, background: "#ede9fe", color: "#7c3aed", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Inter',sans-serif" }}>
              ✍️ Raccogli firma cliente
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva}>Salva rapportino</button>
        </div>
      </div>
    </div>
  );
}

// ── Card rapportino ─────────────────────────────────────────────────────────
// Documento del rapportino su carta intestata (utils/cartaIntestata.js): foglio A4 centrato.
// Unica versione, usata dalla scheda Rapportini della commessa e dal Registro rapportini.
export function apriDocumentoRapportino(r, orgInfo, projectName) {
  const stato = STATI[r.stato] || STATI.bozza;
  const testo = (t) => esc(t).replace(/\n/g, "<br/>");
  const sezione = (titolo, contenuto, classe = "") => contenuto ? `<h2>${titolo}</h2><div class="txt ${classe}">${contenuto}</div>` : "";
  const esito = STATI_INTERVENTO[r.esitoIntervento];
  const materiali = (r.materiali || []).filter(m => String(m.descrizione || "").trim());
  const operatori = (r.operatori || []).filter(Boolean).map((nome, i) => {
    const o = r.orePerOperatore?.[r.operatoriUid?.[i] || nome];
    return esc(nome) + (o ? ` <span class="ore">(${String(o).replace(".", ",")} h)</span>` : "");
  }).join(", ");
  const luogo = descrizioneSede(r.sede) || r.sitoOggetto || "";
  const checklist = (r.checklistManutenzione || []);
  const css = `
    h2{font-size:10px;font-weight:800;color:#374151;margin:14px 0 5px;padding-bottom:3px;border-bottom:2px solid #e5e7eb;text-transform:uppercase;letter-spacing:.05em;}
    .txt{font-size:11.5px;line-height:1.6;}
    .rosso{color:#b91c1c;}
    .cliente{border:2px solid #7c3aed;background:#faf5ff;border-radius:7px;padding:9px 12px;margin-bottom:12px;display:grid;grid-template-columns:1fr auto;gap:12px;align-items:center;}
    .cliente .t{font-size:8.5px;color:#7c3aed;font-weight:700;text-transform:uppercase;letter-spacing:.05em;}
    .cliente .n{font-size:14px;font-weight:800;color:#4c1d95;}
    .cliente .l{font-size:10.5px;color:#4b5563;margin-top:1px;}
    .esito{padding:4px 12px;border-radius:12px;font-size:10.5px;font-weight:700;white-space:nowrap;}
    .dati{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid #e5e7eb;border-radius:7px;margin-bottom:6px;overflow:hidden;}
    .dati div{padding:7px 10px;border-right:1px solid #e5e7eb;}
    .dati div:last-child{border-right:none;}
    .lbl{font-size:8.5px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;display:block;margin-bottom:2px;}
    .val{font-size:11.5px;font-weight:600;}
    .ore{color:#6b7280;font-weight:400;}
    table{border-collapse:collapse;width:100%;}
    th{background:#5b21b6;color:#fff;padding:5px 8px;text-align:left;font-size:9.5px;}
    td{padding:5px 8px;border:1px solid #e5e7eb;font-size:11px;}
    tr:nth-child(even) td{background:#f9fafb;}
    .ko{border:1px solid #fecaca;background:#fef2f2;border-radius:6px;padding:7px 10px;margin-top:6px;font-size:11px;}
    .foto{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;}
    .foto img{width:100%;height:110px;object-fit:cover;border-radius:5px;border:1px solid #e5e7eb;}
    .firme{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:6px;page-break-inside:avoid;}
    .firma{border:1.5px solid #e5e7eb;border-radius:7px;padding:10px 12px;text-align:center;min-height:96px;}
    .firma .t{font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px;}
    .firma img{max-height:80px;max-width:230px;}
    .firma .v{color:#9ca3af;font-size:11px;padding:18px 0;}`;
  const corpo = testataHtml(orgInfo, {
    tipo: "Rapportino di intervento", numero: r.numero || "", data: r.data ? `del ${dataLunga(r.data)}` : "",
    badge: { testo: stato.label, bg: stato.bg, col: stato.col },
  }) + `
    <div class="cliente"><div><div class="t">Cliente</div><div class="n">${esc(r.nomeCliente) || "—"}</div>${luogo ? `<div class="l">📍 ${esc(luogo)}</div>` : ""}</div>
      ${esito ? `<span class="esito" style="background:${esito.bg};color:${esito.col}">${esc(esito.label)}</span>` : ""}</div>
    <div class="dati">
      <div style="grid-column:span 2"><span class="lbl">Commessa</span><span class="val" style="color:#5b21b6">${esc(r.codiceCommessa ? `[${r.codiceCommessa}] ` : "")}${esc(projectName)}</span></div>
      <div><span class="lbl">Data intervento</span><span class="val">${esc(dataLunga(r.data, true))}</span></div>
      <div><span class="lbl">Orario</span><span class="val">${r.oraInizio || r.oraFine ? `${esc(r.oraInizio || "—")} → ${esc(r.oraFine || "—")}` : "—"}</span></div>
    </div>
    <div class="dati" style="grid-template-columns:3fr 1fr">
      <div><span class="lbl">Operatori</span><span class="val">${operatori || "—"}</span></div>
      <div><span class="lbl">Tipologia</span><span class="val">${esc(r.tipologiaIntervento ? r.tipologiaIntervento.charAt(0).toUpperCase() + r.tipologiaIntervento.slice(1) : "—")}</span></div>
    </div>
    ${sezione("Motivo dell'intervento", testo(r.motivoIntervento))}
    ${sezione("Problema riscontrato", testo(r.problemRiscontrato))}
    ${sezione("Diagnosi", testo(r.diagnosi))}
    ${sezione("Lavorazioni eseguite", testo(r.lavorazioni) || "—")}
    ${sezione("Soluzione adottata", testo(r.soluzionAdottata))}
    ${checklist.length ? `<h2>Voci di manutenzione</h2><table><thead><tr><th>Voce</th><th style="width:70px;text-align:center">Effettuato</th><th style="width:50px;text-align:center">N.P.</th><th style="width:60px;text-align:center">Esito</th></tr></thead><tbody>
      ${checklist.map(v => `<tr><td>${esc(v.testo)}</td><td style="text-align:center">${v.fatto ? "✔" : ""}</td><td style="text-align:center">${v.np ? "✔" : ""}</td><td style="text-align:center;font-weight:700;color:${v.esito === "negativo" ? "#dc2626" : "#059669"}">${v.esito === "positivo" ? "✔" : v.esito === "negativo" ? "✖" : ""}</td></tr>`).join("")}</tbody></table>
      ${checklist.filter(v => v.esito === "negativo").map(v => `<div class="ko"><strong>${esc(v.testo)}</strong><br/>Esito negativo: ${esc(v.anomalia || "—")}<br/>Misure correttive: ${esc(v.misure || "—")}</div>`).join("")}` : ""}
    <h2>Materiali utilizzati</h2>
    <table><thead><tr><th>Materiale</th><th style="width:110px;text-align:center">Quantità</th></tr></thead><tbody>
      ${materiali.length ? materiali.map(m => `<tr><td>${esc(m.descrizione)}</td><td style="text-align:center">${esc(m.quantita || "—")}</td></tr>`).join("") : `<tr><td colspan="2" style="color:#9ca3af">Nessun materiale indicato</td></tr>`}</tbody></table>
    ${sezione("Anomalie residue", testo(r.anomalieResidui), "rosso")}
    ${sezione("Attività ancora da svolgere", testo(r.attivitaRestanti))}
    ${sezione("Criticità riscontrate", testo(r.criticita), "rosso")}
    ${sezione("Soluzione proposta", testo(r.soluzione))}
    ${sezione("Note aggiuntive", testo(r.note))}
    ${(r.foto || []).length ? `<h2>Documentazione fotografica</h2><div class="foto">${r.foto.slice(0, 8).map(f => `<img src="${esc(f.data)}" alt=""/>`).join("")}</div>` : ""}
    <h2>Firme</h2>
    <div class="firme">
      <div class="firma"><div class="t">Il tecnico</div>${r.firmaTecnico ? `<img src="${esc(r.firmaTecnico)}" alt=""/>` : `<div class="v">Firma non ancora raccolta</div>`}</div>
      <div class="firma"><div class="t">Il cliente</div>${r.firmaCliente ? `<img src="${esc(r.firmaCliente)}" alt=""/><div style="margin-top:4px;font-size:10.5px">${esc(r.nomeFirmatario || "")}</div>` : `<div class="v">Firma non ancora raccolta</div>`}</div>
    </div>`;
  const piede = piedeHtml(orgInfo, [r.numero || "", `stampato il ${new Date().toLocaleDateString("it-IT")}`]);
  apriDocumento({ titolo: `Rapportino ${r.numero || ""} - ${projectName || ""}`, org: orgInfo, css, corpo, piede });
}

function RapportinoCard({ r, orgInfo, projectName, onEdit, onDelete, onFirmaCliente }) {
  const blocco = statoBlocco(r);
  const [aperto, setAperto] = useState(false);
  const stato = STATI[r.stato] || STATI.bozza;

  const stampaPDF = () => apriDocumentoRapportino(r, orgInfo, projectName);

  const scaricaPDF = () => {
    // Apri in nuova tab per permettere salvataggio manuale
    stampaPDF();
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10, overflow: "hidden" }}>
      {/* sul telefono i pulsanti vanno sotto, invece di stringere il titolo in una colonnina */}
      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, cursor: "pointer" }} onClick={() => setAperto(!aperto)}>
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, flexWrap:"wrap" }}>
            <FileText size={13} color="#7c3aed" />
            {r.numero && <span style={{ fontSize:13, fontWeight:800, color:"#5b21b6", fontFamily:"monospace" }}>{r.numero}</span>}
            {r.numero && projectName && <span style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>— Commessa: {r.codiceCommessa?`[${r.codiceCommessa}] `:""}{projectName}</span>}
            <span style={{ fontSize: 12, fontWeight: 600, color:"#374151" }}>
              {new Date(r.data + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
            </span>
            {r.esitoIntervento && STATI_INTERVENTO[r.esitoIntervento] && (
                <span style={{fontSize:10,fontWeight:700,
                  color:STATI_INTERVENTO[r.esitoIntervento].col,
                  background:STATI_INTERVENTO[r.esitoIntervento].bg,
                  padding:"1px 7px",borderRadius:10}}>
                  {STATI_INTERVENTO[r.esitoIntervento].label}
                </span>
              )}
              {blocco === "definitivo" && <span title="Firmato dal tecnico e dal cliente: non più modificabile" style={{ fontSize: 10, fontWeight: 700, color: "#5b21b6", background: "#ede9fe", padding: "1px 7px", borderRadius: 10 }}>🔒 Firmato · definitivo</span>}
              {blocco === "tecnico" && <span title="Manca la firma del cliente. Modificabile solo da titolare e Project Manager" style={{ fontSize: 10, fontWeight: 700, color: "#92400e", background: "#fef3c7", padding: "1px 7px", borderRadius: 10 }}>✍️ Firmato dal tecnico</span>}
              {blocco === "libero" && r.firmaCliente && <span style={{ fontSize: 10, color: "#059669" }}>✍️ Firmato dal cliente</span>}
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            👷 {(r.operatori || []).join(", ")}
            {r.oraInizio && ` · ${r.oraInizio}${r.oraFine ? " → " + r.oraFine : ""}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={e => { e.stopPropagation(); stampaPDF(); }}
            style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, padding: "3px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "'Inter',sans-serif", color: "#374151" }}
            title="Stampa / Scarica PDF">
            <Printer size={11} /> Stampa/PDF
          </button>
          {blocco === "tecnico" && (
            <button onClick={e => { e.stopPropagation(); onFirmaCliente(); }}
              style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #7c3aed", borderRadius: 6, background: "#ede9fe", cursor: "pointer", fontFamily: "'Inter',sans-serif", color: "#7c3aed", fontWeight: 600 }}>
              ✍️ Firma cliente
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); onEdit(); }}
            title={blocco === "definitivo" ? "Rapportino firmato: non più modificabile" : undefined}
            style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "'Inter',sans-serif", color: blocco === "definitivo" ? "#a78bfa" : "#6b7280" }}>
            {blocco === "definitivo" ? "🔒 Modifica" : "Modifica"}
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}
            onMouseOver={e => e.currentTarget.style.color = "#dc2626"} onMouseOut={e => e.currentTarget.style.color = "#fca5a5"}>
            <X size={13} />
          </button>
          {aperto ? <ChevronDown size={14} color="#9ca3af" /> : <ChevronRight size={14} color="#9ca3af" />}
        </div>
      </div>

      {aperto && (
        <div style={{ borderTop: "1px solid #f3f4f6", padding: "12px 16px" }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>🔧 Lavorazioni eseguite</div>
            <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{r.lavorazioni || "—"}</div>
          </div>
          {(r.checklistManutenzione||[]).length>0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>📋 Voci di manutenzione</div>
              <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) repeat(4,40px)", gap:2, fontSize:8.5, fontWeight:800, color:"#6b7280", textTransform:"uppercase", textAlign:"center", marginBottom:2 }}>
                <div style={{textAlign:"left"}}>Voce</div><div>Eff.</div><div>N.P.</div><div style={{color:"#059669"}}>Esito +</div><div style={{color:"#dc2626"}}>Esito −</div>
              </div>
              {r.checklistManutenzione.map((v,i)=>(
                <div key={i} style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) repeat(4,40px)", gap:2, fontSize:12, padding:"2px 0", borderTop:"1px solid #f3f4f6", background: v.esito==="negativo"?"#fef2f2":"transparent" }}>
                  <div style={{ color: v.np?"#9ca3af":"#374151" }}>{v.testo}</div>
                  <div style={{textAlign:"center"}}>{v.fatto?"✔":""}</div>
                  <div style={{textAlign:"center"}}>{v.np?"✔":""}</div>
                  <div style={{textAlign:"center",color:"#059669"}}>{v.esito==="positivo"?"✔":""}</div>
                  <div style={{textAlign:"center",color:"#dc2626"}}>{v.esito==="negativo"?"✕":""}</div>
                </div>
              ))}
              {r.checklistManutenzione.filter(v=>v.esito==="negativo").map((v,i)=>(
                <div key={"n"+i} style={{ marginTop:6, padding:"8px 10px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, fontSize:11.5 }}>
                  <strong>{v.testo}</strong><br/>
                  <span style={{color:"#6b7280"}}>Esito negativo:</span> {v.anomalia||"—"}<br/>
                  <span style={{color:"#6b7280"}}>Misure correttive:</span> {v.misure||"—"}
                </div>
              ))}
            </div>
          )}
          {(r.materiali || []).some(m => m.descrizione) && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>📦 Materiali utilizzati</div>
              {r.materiali.filter(m => m.descrizione).map((m, i) => (
                <div key={i} style={{ fontSize: 12, color: "#6b7280" }}>• {m.descrizione}{m.quantita ? ` — ${m.quantita}` : ""}</div>
              ))}
            </div>
          )}
          {r.problemRiscontrato && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>🔍 Problema riscontrato</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.problemRiscontrato}</div>
            </div>
          )}
          {r.diagnosi && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#d97706", marginBottom: 4 }}>🧪 Diagnosi</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.diagnosi}</div>
            </div>
          )}
          {r.soluzionAdottata && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", marginBottom: 4 }}>✅ Soluzione adottata</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.soluzionAdottata}</div>
            </div>
          )}
          {r.anomalieResidui && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>⚠️ Anomalie residue</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.anomalieResidui}</div>
            </div>
          )}
          {r.attivitaRestanti && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 4 }}>📋 Attività ancora da eseguire</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.attivitaRestanti}</div>
            </div>
          )}
          {r.criticita && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>⚠️ Criticità</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.criticita}</div>
            </div>
          )}
          {r.soluzione && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", marginBottom: 4 }}>💡 Soluzione proposta</div>
              <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.soluzione}</div>
            </div>
          )}
          {(r.foto||[]).length>0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>📷 Foto</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {(r.foto||[]).map((f,i)=>(
                  <img key={i} src={f.data} alt={f.nome||"foto"} style={{width:80,height:80,objectFit:"cover",borderRadius:6,border:"1px solid #e5e7eb",cursor:"pointer"}}
                    onClick={()=>window.open(f.data,"_blank")}/>
                ))}
              </div>
            </div>
          )}
          {r.firmaTecnico && (
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#f4f0fc", borderRadius: 8, border: "1px solid #ddd6fe" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#5b21b6", marginBottom: 6 }}>✍️ Firma tecnico</div>
              <FirmaImmagine src={r.firmaTecnico} alt="Firma tecnico" style={{ maxHeight: 80, maxWidth: 250, display: "block" }} />
            </div>
          )}
          {r.firmaCliente && (
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #6ee7b7" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#065f46", marginBottom: 6 }}>✍️ Firma cliente — {r.nomeFirmatario || "anonimo"}</div>
              <FirmaImmagine src={r.firmaCliente} alt="Firma" style={{ maxHeight: 80, maxWidth: 250, display: "block" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────────
// Firma del cliente su un rapportino già firmato dal tecnico: è l'unica modifica che può fare
// chiunque (le regole del database consentono solo firmaCliente/nomeFirmatario/stato/updatedAt).
function FirmaClienteModal({ r, onClose }) {
  const { addToast } = useToast();
  const [nome, setNome] = useState(r.nomeFirmatario || "");
  const [salvando, setSalvando] = useState(false);
  const salva = async (firma) => {
    if (!nome.trim()) { addToast("Scrivi il nome di chi firma per il cliente", "error"); return; }
    setSalvando(true);
    try {
      await updateDoc(doc(db, "rapportini", r.id), { firmaCliente: firma, nomeFirmatario: nome.trim(), stato: "firmato", updatedAt: serverTimestamp() });
      addToast(`Rapportino ${r.numero || ""} firmato dal cliente ✅`, "success");
      onClose();
    } catch (e) {
      addToast("Firma non salvata: " + (e?.message || e), "error");
      setSalvando(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>✍️ Firma del cliente — {r.numero || "rapportino"}</h3>
        <p style={{ fontSize: 12, color: "#5b21b6", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "8px 10px", margin: "8px 0 14px", lineHeight: 1.5 }}>
          Con la firma del cliente il rapportino diventa un documento definitivo: dopo nessuno potrà più modificarlo né eliminarlo.
        </p>
        <div className="form-group">
          <label className="form-label">Nome del firmatario</label>
          <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome e cognome cliente"
            style={{ width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" }} />
        </div>
        {salvando ? <div style={{ fontSize: 12, color: "#6b7280" }}>Salvataggio…</div>
          : <FirmaCanvas onSave={salva} onCancel={onClose} />}
      </div>
    </div>
  );
}

export default function RapportiniTab({ projectId, projectName, codiceCommessa, orgId }) {
  const { currentUser, userProfile, isPlatformOwner } = useAuth();
  const [firmaClienteDi, setFirmaClienteDi] = useState(null);
  // Dati della commessa per proporre cliente e luogo (sede dei lavori)
  const [commessa, setCommessa] = useState(null);
  useEffect(() => { if (projectId) getDoc(doc(db, "projects", projectId)).then(s => setCommessa(s.exists() ? s.data() : null)).catch(() => {}); }, [projectId]);
  const { addToast } = useToast();
  const [rapportini, setRapportini] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editRap, setEditRap] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [utenti, setUtenti] = useState([]);
  // Rapportini iniziati e non salvati su questo dispositivo (utils/bozzeLocali.js)
  const [riprendiBozza, setRiprendiBozza] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d=>d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"))));
  }, [orgId]);

  useEffect(() => {
    if (!projectId || !orgId) return;
    const q = query(collection(db, "rapportini"), where("projectId", "==", projectId), where("orgId", "==", orgId));
    return onSnapshot(q, snap => setRapportini(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.data.localeCompare(a.data))));
  }, [projectId, orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db, "organizations", orgId)).then(snap => { if (snap.exists()) setOrgInfo(snap.data()); });
  }, [orgId]);

  // Blocco dopo le firme (utils/bloccoRapportini.js): avviso viola invece di aprire/eliminare
  const ctxBlocco = { uid: currentUser?.uid, role: userProfile?.role, ruoloOperativo: userProfile?.ruoloOperativo, isPlatformOwner, commessa };
  const bloccato = (r) => {
    const v = verificaModificaRapportino(r, ctxBlocco);
    if (v.ok) return false;
    addToast(v.avviso + (statoBlocco(r) === "tecnico" ? AVVISO_AGGIUNGI_FIRMA_CLIENTE : ""), "viola", 9000);
    return true;
  };
  const modifica = (r) => { if (bloccato(r)) return; setRiprendiBozza(false); setEditRap(r); setShowModal(true); };
  const riprendiDa = (r) => {
    if (r && bloccato(r)) return;
    setEditRap(r); setRiprendiBozza(true); setShowModal(true);
  };

  const elimina = async (r) => {
    if (bloccato(r)) return;
    const id = r.id;
    if (!await confirm("Eliminare questo rapportino?", { type:"danger" })) return;
    await deleteDoc(doc(db, "rapportini", id));
    addToast("Eliminato", "info");
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
            <FileText size={15} color="#7c3aed" /> Rapportini di intervento
          </h3>
          <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0" }}>{rapportini.length} rapportino/i</p>
        </div>
        <button onClick={() => { setEditRap(null); setRiprendiBozza(false); setShowModal(true); }} className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Plus size={13} /> Nuovo rapportino
        </button>
      </div>

      {/* rapportini iniziati e non salvati su questo dispositivo */}
      {!showModal && <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "rapportino", projectId)} documenti={rapportini}
        testoDi={(b, r) => r ? `Modifiche non salvate al rapportino ${r.numero || ""}` : `Rapportino non salvato${delGiorno(b.info?.data)}`}
        onRiprendi={riprendiDa} />}

      {rapportini.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
          <FileText size={48} style={{ opacity: 0.15, marginBottom: 16 }} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>Nessun rapportino</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Crea il primo rapportino di intervento per questa commessa</div>
        </div>
      ) : rapportini.map(r => (
        <RapportinoCard key={r.id} r={r} orgInfo={orgInfo} projectName={projectName}
          onEdit={() => modifica(r)}
          onDelete={() => elimina(r)}
          onFirmaCliente={() => setFirmaClienteDi(r)} />
      ))}

      {showModal && (
        <RapportinoModal
          commessa={commessa}
          rapportino={editRap}
          projectId={projectId}
          projectName={projectName}
          codiceCommessa={codiceCommessa}
          orgId={orgId}
          orgInfo={orgInfo}
          utenti={utenti}
          riprendiBozza={riprendiBozza}
          onClose={() => { setShowModal(false); setEditRap(null); setRiprendiBozza(false); }} />
      )}
      {firmaClienteDi && <FirmaClienteModal r={firmaClienteDi} onClose={() => setFirmaClienteDi(null)} />}
    </div>
  );
}
